import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Form,
  Row,
  Col,
  Typography,
  Select,
  Button,
  Modal,
  Input,
  DatePicker,
  Table,
  InputNumber,
  AutoComplete,
  message,
  Grid,
  Card,
  Space
} from 'antd';
import { PlusOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useUser } from '../context/UserContext';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Helpers de formato para InputNumber (miles con punto)
const numberFormatter = (value) => {
  if (value === null || value === undefined) return '';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};
const numberParser = (value) => {
  if (value === null || value === undefined) return 0;
  return Number(value.toString().replace(/\./g, '')) || 0;
};

// Helper para mostrar el Total general en <Input />
const formatNumberForDisplay = (n) => new Intl.NumberFormat('es-CO').format(Number(n || 0));

export default function Frmentrada() {
  const { user, loading } = useUser();
  const storedUser = useMemo(() => {
    const usuarioId = localStorage.getItem('usuarioId');
    const nombreUsuario = localStorage.getItem('nombreUsuario');
    if (usuarioId) {
      return { usuarioId: Number(usuarioId), nombreUsuario };
    }
    return null;
  }, []);
  const currentUser = user || storedUser;

  const [form] = Form.useForm();
  const [provForm] = Form.useForm();
  const [providers, setProviders] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [productOptions, setProductOptions] = useState({});
  const [unitOptions, setUnitOptions] = useState({});
  const [tableError, setTableError] = useState('');
  const [successVisible, setSuccessVisible] = useState(false);
  const searchTimeout = useRef(null);
  const providerSearchTimeout = useRef(null);
  const screens = useBreakpoint();

  useEffect(() => {
    fetchProviders();
    setDataSource([createEmptyRow()]);
    return () => {
      clearTimeout(searchTimeout.current);
      clearTimeout(providerSearchTimeout.current);
    };
  }, []);

  if (loading) return <p>Cargando...</p>;

  function createEmptyRow() {
    return {
      key: Date.now(),
      productoId: null,
      productoNombre: '',
      tipoMedidaId: null,
      unidadMedidaId: null,
      unidadFactorBase: null,
      presentacionCantidad: null,
      cantidad: null,
      precioUnitario: null,
      total: 0,
      // Centro de Costo por fila
      centroCostoId: null,
      ccOptions: []
    };
  }

  async function fetchProviders(nombre = '') {
    try {
      const url = nombre
        ? `/Proveedores/buscar?nombre=${encodeURIComponent(nombre)}`
        : '/Proveedores';

      const res = await api.get(url);
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.$values)
        ? res.data.$values
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];

      setProviders(list);
    } catch (err) {
      console.error('Error al cargar proveedores:', err?.response?.data || err);
      message.error('Error al cargar proveedores.');
    }
  }

  function searchProvidersDebounced(value) {
    clearTimeout(providerSearchTimeout.current);
    providerSearchTimeout.current = setTimeout(() => {
      fetchProviders(value);
    }, 400);
  }

  async function handleAddProvider() {
    try {
      const vals = await provForm.validateFields();
      const payload = {
        documento: vals.Documento,
        direccion: vals.Direccion,
        telefono: Number(vals.Telefono),
        estado: 1,
        nombre: vals.Nombre
      };
      await api.post('/Proveedores', payload);
      message.success('Proveedor agregado exitosamente');
      setModalVisible(false);
      provForm.resetFields();
      fetchProviders('');
    } catch (err) {
      console.error('Error POST /Proveedores:', err?.response?.data || err);
      message.error('Error al guardar proveedor');
    }
  }

  function handleSearchProduct(value, key) {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      if (!value) return;
      try {
        const res = await api.get('/Producto/buscar?nombre=' + encodeURIComponent(value));
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.$values)
          ? res.data.$values
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        setProductOptions((opts) => ({ ...opts, [key]: raw }));
      } catch (err) {
        console.error('Error GET /Producto/buscar:', err?.response?.data || err);
      }
    }, 300);
  }

  async function fetchProductById(id) {
    try {
      const res = await api.get('/Producto/' + id);
      return res?.data || null;
    } catch (err) {
      console.error('Error GET /Producto/{id}:', err?.response?.data || err);
      return null;
    }
  }

  async function handleProductSelect(rowKey, nombre, opt) {
    handleTableChange(rowKey, 'productoNombre', nombre);
    handleTableChange(rowKey, 'productoId', opt.id);

    let tipoId = opt.tipoMedidaId;
    let idCentroCosto = opt.idCentrosCosto ?? opt.IdCentrosCosto ?? null;

    if (!tipoId || !idCentroCosto) {
      const full = await fetchProductById(opt.id);
      tipoId = tipoId ?? (full?.tipoMedidaId ?? full?.TipoMedidaId ?? null);
      idCentroCosto = idCentroCosto ?? (full?.idCentrosCosto ?? full?.IdCentrosCosto ?? null);
    }

    handleTableChange(rowKey, 'tipoMedidaId', tipoId);
    handleTableChange(rowKey, 'unidadMedidaId', null);
    handleTableChange(rowKey, 'unidadFactorBase', null);

    // reset CC y cargarlo si existe
    handleTableChange(rowKey, 'centroCostoId', null);
    handleTableChange(rowKey, 'ccOptions', []);

    if (tipoId) await loadUnitsForRow(rowKey, tipoId);
    if (idCentroCosto) await loadCentroCostoForRow(rowKey, idCentroCosto);
  }

  async function loadUnitsForRow(rowKey, tipoMedidaId) {
    if (!tipoMedidaId) return;
    try {
      const res = await api.get('/UnidadMedida/tipo/' + tipoMedidaId + '/activos');
      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.$values)
        ? res.data.$values
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      setUnitOptions((prev) => ({ ...prev, [rowKey]: raw }));
    } catch (err) {
      console.error('Error GET /UnidadMedida/tipo/{id}/activos:', err?.response?.data || err);
      message.error('Error al cargar unidades de medida.');
    }
  }

  // Cargar Centro de Costo único por fila usando el IdCentrosCosto del producto
  async function loadCentroCostoForRow(rowKey, idCentroCosto) {
    if (!idCentroCosto) {
      setDataSource((prev) => prev.map(r => r.key === rowKey ? { ...r, centroCostoId: null, ccOptions: [] } : r));
      return;
    }
    try {
      const res = await api.get('/CentroCosto/' + idCentroCosto);
      const item = res?.data;
      const option = item ? [{ value: item.id ?? item.Id, label: item.nombre ?? item.Nombre }] : [];
      setDataSource((prev) => prev.map(r => (
        r.key === rowKey
          ? { ...r, centroCostoId: (item?.id ?? item?.Id) ?? null, ccOptions: option }
          : r
      )));
    } catch (err) {
      console.error('Error GET /CentroCosto/{id}:', err?.response?.data || err);
      setDataSource((prev) => prev.map(r => r.key === rowKey ? { ...r, centroCostoId: null, ccOptions: [] } : r));
    }
  }

  // Cargar TODOS los Centros de Costo para permitir cambiar el preseleccionado
  async function loadAllCentroCostosForRow(rowKey, keepSelectedId = null) {
    try {
      const res = await api.get('/CentroCosto');
      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.$values)
        ? res.data.$values
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      const opts = raw.map(c => ({ value: c.id ?? c.Id, label: c.nombre ?? c.Nombre }));
      setDataSource(prev => prev.map(r => (
        r.key === rowKey
          ? { ...r, ccOptions: opts, centroCostoId: (keepSelectedId ?? r.centroCostoId ?? (opts[0]?.value ?? null)) }
          : r
      )));
    } catch (err) {
      console.error('Error GET /CentroCosto:', err?.response?.data || err);
    }
  }

  function handleTableChange(key, field, value) {
    setDataSource((prev) => {
      const newData = prev.map((row) => {
        if (row.key === key) {
          const updated = { ...row, [field]: value };
          if (['cantidad', 'precioUnitario'].includes(field)) {
            updated.total = (updated.cantidad || 0) * (updated.precioUnitario || 0);
          }
          return updated;
        }
        return row;
      });
      const lastRow = prev[prev.length - 1];
      const currentRow = newData.find((r) => r.key === key);
      if (
        currentRow &&
        currentRow.productoId &&
        currentRow.cantidad != null &&
        currentRow.precioUnitario != null &&
        lastRow.key === key
      ) {
        newData.push(createEmptyRow());
      }
      return newData;
    });
  }

  function calculateGrandTotal() {
    return dataSource.reduce((sum, r) => sum + (r.total || 0), 0);
  }

  function handleDeleteRow(key) {
    setDataSource((prev) => prev.filter((r) => r.key !== key));
  }

  function handleSuccessClose() {
    setSuccessVisible(false);
    form.resetFields();
    setDataSource([createEmptyRow()]);
    setTableError('');
  }

  // 🔽 Utilidad para redondear a entero (sin decimales)
  const roundInt = (n) => {
    if (n === null || n === undefined || isNaN(n)) return 0;
    return Math.round(Number(n));
  };

  async function handleSave() {
    if (!currentUser) {
      message.error('Usuario no autenticado');
      return;
    }
    try {
      const vals = await form.validateFields();
      if (dataSource.length === 0) {
        const msg = 'Debe existir al menos una fila en la tabla antes de guardar.';
        setTableError(msg);
        message.warning(msg);
        return;
      }
      const validRows = dataSource.filter((r) => {
        return r.productoId && r.cantidad != null && r.precioUnitario != null && r.unidadMedidaId;
      });
      if (!validRows.length) {
        const msg = 'Debe agregar al menos una fila completa (producto, cantidad, medida y precio unitario).';
        setTableError(msg);
        message.warning(msg);
        return;
      }

      setTableError('');
      const total = calculateGrandTotal();
      const entradaPayload = {
        fechaEntrada: vals.fecha.format('YYYY-MM-DD'),
        idProveedor: vals.ddlProveedor,
        docEntrada: vals.txtNumero,
        usuario: currentUser.usuarioId,
        usuarioAnula: null,
        fechaElaboracion: new Date().toISOString(),
        total: total,
        saldo: total,
        estado: 1
      };

      // 1) Crear Entrada
      const entradaRes = await api.post('/Entrada', entradaPayload);
      const entradaId = entradaRes?.data?.id ?? entradaRes?.data?.Id;
      if (!entradaId && entradaId !== 0) {
        message.error('No se recibió el Id de la entrada desde el servidor.');
        return;
      }

      // 2) Detalles + Inventario + PlanConsumo/DetallePlanConsumo
      for (const row of validRows) {
        // Conversión a unidad base para inventario y CantidadActual del detalle
        const factor = row.unidadFactorBase ?? 1;
        const present = row.presentacionCantidad ?? 1;
        const cantidadTransformada = (row.cantidad || 0) * factor * present;

        // 2.1) DetalleEntrada (Cantidad = digitada, CantidadActual = transformada)
        const detallePayload = {
          IdEntrada: entradaId,
          IdProducto: row.productoId,
          ValorCompra: row.total,
          Cantidad: row.cantidad,               // digitada
          CantidadActual: cantidadTransformada, // convertida
          PrecioUnitario: row.precioUnitario,
          idUnidadMedida: row.unidadMedidaId,
          UnidadMedidaId: row.unidadMedidaId,
          Vencimiento: new Date().toISOString()
        };
        const detalleRes = await api.post('/DetalleEntrada', detallePayload);
        const detalleId = detalleRes?.data?.id ?? detalleRes?.data?.Id ?? null;

        // 2.1.b) DetalleCentroCosto (si hay CC seleccionado)
        try {
          const ccId = (row.centroCostoId ?? (Array.isArray(row.ccOptions) ? row.ccOptions[0]?.value : null)) ?? null;
          if (detalleId && ccId) {
            const dccObj = { id: 0, idCentroCosto: ccId, idDetalleEntrada: detalleId, estado: 1 };
          try {
            await api.post('/DetalleCentroCosto', dccObj);
          } catch (e1) {
            if (e1?.response?.status === 400) {
              await api.post('/DetalleCentroCosto', [dccObj]);
            } else {
              throw e1;
            }
          }
          }
        } catch (err) {
          console.error('Error POST /DetalleCentroCosto:', err?.response?.data || err);
        }

        // 2.2) Inventario: PUT cantidad por IdProducto (o POST si 404)
        let idInventario = null;
        let cantidadInventario = null;
        try {
          const invRes = await api.get('/Inventario/producto/' + row.productoId);
          const existing = invRes?.data;
          const existingQty = Number(existing?.cantidad ?? existing?.Cantidad ?? 0);
          const newCantidad = existingQty + cantidadTransformada;

          // PUT minimalista por producto/cantidad
          await api.put(`/Inventario/producto/${row.productoId}/cantidad`, {
            cantidad: Number(newCantidad)
          });

          idInventario = existing?.id ?? existing?.Id ?? null;
          cantidadInventario = Number(newCantidad);
        } catch (err) {
          if (err?.response?.status === 404 || err?.message?.includes('sin Id')) {
            const postInv = {
              Id: 0,
              IdProducto: row.productoId,
              Producto: { Id: row.productoId },
              Cantidad: Number(cantidadTransformada),
              Estado: 1,
              Imagen: 'string'
            };
            const postRes = await api.post('/Inventario', postInv, {
              headers: { 'Content-Type': 'application/json' }
            });
            idInventario = postRes?.data?.id ?? postRes?.data?.Id ?? null;
            cantidadInventario = Number(postRes?.data?.cantidad ?? postRes?.data?.Cantidad ?? cantidadTransformada);
          } else {
            console.error('Error actualizando inventario:', err?.response?.data || err);
            throw err;
          }
        }

        // 2.3) Consultar PlanConsumo por producto (GET con params)
        let plan = null;
        try {
          const planRes = await api.get('/PlanConsumo', {
            params: { idProducto: row.productoId, estado: 1 }
          });
          const arr = Array.isArray(planRes.data)
            ? planRes.data
            : Array.isArray(planRes.data?.$values)
            ? planRes.data.$values
            : planRes.data?.data && Array.isArray(planRes.data.data)
            ? planRes.data.data
            : [];
          plan = arr[0] || null; // tomamos el primero si hubiera varios
        } catch (err) {
          console.warn('Sin PlanConsumo para producto', row.productoId, err?.response?.data || err);
        }

        // 2.4) Si NO hay plan, NO ejecutar ni PUT ni POST de DetallePlanConsumo
        if (!plan || idInventario == null || cantidadInventario == null) {
          continue;
        }

        // 2.4.b) Calcular consumoDiario / diasConsumo según peso o numDias
        const idPlan = plan.id ?? plan.Id ?? null;
        const peso = plan.peso ?? plan.Peso;          // puede ser null
        const numDias = plan.numDias ?? plan.NumDias; // puede ser null

        let consumoDiario = 0;
        let diasConsumo = 0;
        if (peso != null) {
          diasConsumo = roundInt(Number(cantidadInventario) / Number(peso || 1));
          consumoDiario = roundInt(Number(peso));
        } else if (numDias != null && Number(numDias) > 0) {
          consumoDiario = roundInt(Number(cantidadInventario) / Number(numDias || 1));
          diasConsumo = Number(numDias);
        }

        // 2.4.c) Cerrar/actualizar estados previos de detalles para este plan (PUT)
        try {
          await api.put(`/DetallePlanConsumo/estado-por-plan/${idPlan}`);
        } catch (err) {
          console.warn('PUT /DetallePlanConsumo/estado-por-plan falló:', err?.response?.data || err);
        }

        // 2.5) Crear DetallePlanConsumo
        const dpcPayload = {
          idInventario: idInventario,
          idPlanConsumo: idPlan,
          fecha: new Date().toISOString(),
          estado: 1,
          consumoDiario: Number(consumoDiario),
          diasConsumo: Number(diasConsumo),
          cantidadActual: Number(cantidadInventario)
        };
        try {
          await api.post('/DetallePlanConsumo', dpcPayload);
        } catch (err) {
          console.error('Error POST /DetallePlanConsumo:', err?.response?.data || err);
        }
      }

      setSuccessVisible(true);
    } catch (err) {
      console.error('Error guardando entrada:', err?.response?.data || err);
      message.error('Ocurrió un error al guardar la entrada.');
    }
  }

  const columns = [
    {
      title: 'Producto',
      dataIndex: 'productoNombre',
      width: 250,
      render: function (text, record) {
        const options = (Array.isArray(productOptions[record.key]) ? productOptions[record.key] : []).map(function (p) {
          return {
            value: p.nombre ?? p.Nombre,
            label: p.nombre ?? p.Nombre,
            id: p.id ?? p.Id,
            tipoMedidaId: p.tipoMedidaId ?? p.TipoMedidaId,
            idCentrosCosto: p.idCentrosCosto ?? p.IdCentrosCosto
          };
        });
        return (
          <AutoComplete
            value={text}
            options={options}
            onSearch={function (val) { return handleSearchProduct(val, record.key); }}
            onSelect={function (val, opt) { handleProductSelect(record.key, val, opt); }}
            onChange={function (val) { handleTableChange(record.key, 'productoNombre', val); }}
            style={{ width: '100%' }}
          />
        );
      }
    },
    {
      title: 'CC',
      dataIndex: 'centroCostoId',
      width: 110,
      render: (val, record) => (
        <Select
          placeholder="Centro de costo"
          value={record.centroCostoId}
          options={record.ccOptions || []}
          onChange={(id) => handleTableChange(record.key, 'centroCostoId', id)}
          onDropdownVisibleChange={(open) => { if (open) loadAllCentroCostosForRow(record.key, record.centroCostoId ?? null); }}
          dropdownMatchSelectWidth={false}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Cant',
      dataIndex: 'cantidad',
      width: 80,
      render: function (val, record) {
        return (
          <InputNumber
            min={0}
            value={val}
            onChange={function (num) { handleTableChange(record.key, 'cantidad', num); }}
            style={{ width: '100%' }}
          />
        );
      }
    },
    {
      title: 'Presentación',
      dataIndex: 'unidadMedidaId',
      width: 260,
      render: function (val, record) {
        return (
          <div style={{ display: 'flex', gap: 8, flexWrap: screens.xs ? 'wrap' : 'nowrap' }}>
            <InputNumber
              min={0}
              precision={0}
              step={1}
              placeholder={'Cant Presentación'}
              value={record.presentacionCantidad}
              onChange={function (num) { handleTableChange(record.key, 'presentacionCantidad', num); }}
              style={{ flex: screens.xs ? '1 1 100%' : '0 0 80px' }}
              disabled={!((unitOptions[record.key] || []).length)}
            />
            <Select
              showSearch
              placeholder={record.tipoMedidaId ? 'Seleccione medida' : 'Seleccione presentación'}
              value={val}
              onChange={function (id) {
                handleTableChange(record.key, 'unidadMedidaId', id);
                const list = unitOptions[record.key] || [];
                const u = list.find(function (x) { return (x.id ?? x.Id) === id; });
                const fb = u ? (u.factorBase ?? u.FactorBase ?? null) : null;
                handleTableChange(record.key, 'unidadFactorBase', fb);
              }}
              style={{ flex: 1 }}
              options={(unitOptions[record.key] || []).map(function (u) {
                const abbr = (u.abreviatura ?? u.Abreviatura) || '';
                const name = (u.nombre ?? u.Nombre) || '';
                return { value: u.id ?? u.Id, label: (abbr + (name ? ' (' + name + ')' : '')).trim() };
              })}
              disabled={!((unitOptions[record.key] || []).length)}
              filterOption={function (input, option) {
                const lbl = (option?.label ?? '').toLowerCase();
                return lbl.indexOf(input.toLowerCase()) !== -1;
              }}
            />
          </div>
        );
      }
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'precioUnitario',
      width: 110,
      render: function (val, record) {
        return (
          <InputNumber
            min={0}
            value={val}
            formatter={numberFormatter}
            parser={numberParser}
            onChange={function (num) { handleTableChange(record.key, 'precioUnitario', num); }}
            className="centered-number"
            style={{ width: '100%' }}
          />
        );
      }
    },
    {
      title: 'Precio Total',
      dataIndex: 'total',
      width: 110,
      render: function (_val, record) {
        return (
          <InputNumber
            value={record.total}
            readOnly
            formatter={numberFormatter}
            parser={numberParser}
            className="centered-number"
            style={{ width: '100%' }}
          />
        );
      }
    },
    {
      title: 'Eliminar',
      dataIndex: 'accion',
      width: 80,
      render: function (_val, record) {
        return (
          <Button icon={<DeleteOutlined />} onClick={function () { handleDeleteRow(record.key); }} />
        );
      }
    }
  ];

  return (
      <>
        <style>{`.centered-number input { text-align: center; }`}</style>
        <Form form={form} layout={'vertical'}>
      <Row justify={'center'}>
        <Col md={16} xs={24}>
          <Title level={3} style={{ textAlign: 'center' }}>Ingreso al inventario</Title>

          <Row gutter={16} align={'middle'}>
            <Col flex={'auto'}>
              <Form.Item name={'ddlProveedor'} label={'Proveedor'} rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Seleccione proveedor"
                  filterOption={false}
                  onSearch={searchProvidersDebounced}
                  onDropdownVisibleChange={(open) => { if (open) fetchProviders(''); }}
                  options={providers.map((p) => ({
                    value: p.id ?? p.Id,
                    label: p.nombre ?? p.Nombre
                  }))}
                />
              </Form.Item>
            </Col>
            <Col>
              <Button icon={<PlusOutlined />} onClick={() => setModalVisible(true)} />
            </Col>
          </Row>

          <Modal
            open={modalVisible}
            title={'Agregar Proveedor'}
            onCancel={() => { setModalVisible(false); provForm.resetFields(); }}
            onOk={handleAddProvider}
          >
            <Form form={provForm} layout={'vertical'}>
              <Form.Item name={'Nombre'} label={'Nombre'} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name={'Documento'} label={'Documento'} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name={'Direccion'} label={'Dirección'} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name={'Telefono'} label={'Teléfono'} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            open={successVisible}
            title={'Proceso finalizado completamente'}
            onOk={handleSuccessClose}
            onCancel={handleSuccessClose}
            okText={'Aceptar'}
            cancelButtonProps={{ style: { display: 'none' }}}
            destroyOnClose
          >
            <Text>Se han guardado todos los datos correctamente.</Text>
          </Modal>

          <Row gutter={16} align={'middle'} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Form.Item name={'txtNumero'} label={'Número de factura'} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={'fecha'} label={'Fecha'} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Vista móvil (xs/sm): tarjetas apiladas */}
          {!screens.md ? (
            <Space direction={'vertical'} size={'middle'} style={{ width: '100%' }}>
              {dataSource.map(function (record) {
                return (
                  <Card
                    key={record.key}
                    size={'small'}
                    title={record.productoNombre || 'Producto'}
                    extra={
                      <Button
                        danger
                        type={'text'}
                        icon={<DeleteOutlined />}
                        onClick={function () { handleDeleteRow(record.key); }}
                      />
                    }
                  >
                    <Space direction={'vertical'} size={'small'} style={{ width: '100%' }}>
                      <div>
                        <Text strong>Producto</Text>
                        <AutoComplete
                          value={record.productoNombre}
                          options={(Array.isArray(productOptions[record.key]) ? productOptions[record.key] : []).map(function (p) {
                            return {
                              value: p.nombre ?? p.Nombre,
                              label: p.nombre ?? p.Nombre,
                              id: p.id ?? p.Id,
                              tipoMedidaId: p.tipoMedidaId ?? p.TipoMedidaId,
                              idCentrosCosto: p.idCentrosCosto ?? p.IdCentrosCosto
                            };
                          })}
                          onSearch={function (val) { handleSearchProduct(val, record.key); }}
                          onSelect={function (val, opt) { handleProductSelect(record.key, val, opt); }}
                          onChange={function (val) { handleTableChange(record.key, 'productoNombre', val); }}
                          style={{ width: '100%', marginTop: 6 }}
                        />
                      </div>

                      <div>
                        <Text strong>CC</Text>
                        <Select
                          placeholder={'Centro de costo'}
                          value={record.centroCostoId}
                          options={record.ccOptions || []}
                          onChange={(id) => handleTableChange(record.key, 'centroCostoId', id)}
                          onDropdownVisibleChange={(open) => { if (open && (!record.ccOptions || record.ccOptions.length === 0)) { loadAllCentroCostosForRow(record.key, record.centroCostoId ?? null); } }}
                          dropdownMatchSelectWidth={false}
                          style={{ width: '50%', minWidth: 120, marginTop: 6 }}
                        />
                      </div>

                      <div>
                        <Text strong>Cant</Text>
                        <InputNumber
                          min={0}
                          value={record.cantidad}
                          style={{ width: '50%', marginTop: 6 }}
                        />
                      </div>

                      <div>
                        <Text strong>Presentación</Text>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                          <InputNumber
                            min={0}
                            precision={0}
                            step={1}
                            placeholder={'Cant Presentación'}
                            value={record.presentacionCantidad}
                            onChange={function (num) { handleTableChange(record.key, 'presentacionCantidad', num); }}
                            style={{ flex: '1 1 80px' }}
                            disabled={!((unitOptions[record.key] || []).length)}
                          />
                          <Select
                            showSearch
                            placeholder={record.tipoMedidaId ? 'Seleccione medida' : 'Seleccione presentación'}
                            value={record.unidadMedidaId}
                            onChange={function (id) {
                              handleTableChange(record.key, 'unidadMedidaId', id);
                              const list = unitOptions[record.key] || [];
                              const u = list.find(function (x) { return (x.id ?? x.Id) === id; });
                              const fb = u ? (u.factorBase ?? u.FactorBase ?? null) : null;
                              handleTableChange(record.key, 'unidadFactorBase', fb);
                            }}
                            style={{ flex: '1 1 200px' }}
                            options={(unitOptions[record.key] || []).map(function (u) {
                              const abbr = (u.abreviatura ?? u.Abreviatura) || '';
                              const name = (u.nombre ?? u.Nombre) || '';
                              return { value: u.id ?? u.Id, label: (abbr + (name ? ' (' + name + ')' : '')).trim() };
                            })}
                            disabled={!((unitOptions[record.key] || []).length)}
                            filterOption={function (input, option) {
                              const lbl = (option?.label ?? '').toLowerCase();
                              return lbl.indexOf(input.toLowerCase()) !== -1;
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <Text strong>Precio Unitario</Text>
                        <InputNumber
                          min={0}
                          value={record.precioUnitario}
                          className="centered-number"
                          style={{ width: '50%', marginTop: 6 }}
                        />
                      </div>

                      <div>
                        <Text strong>Precio Total</Text>
                        <InputNumber readOnly value={record.total} className="centered-number" style={{ width: '50%', marginTop: 6 }} />
                      </div>
                    </Space>
                  </Card>
                );
              })}
            </Space>
          ) : (
            // Vista escritorio (md+): tabla con scroll horizontal
            <div style={{ overflowX: 'auto' }}>
              <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                rowKey={'key'}
                size={'small'}
                tableLayout={'fixed'}
                scroll={{ x: 'max-content' }}
              />
            </div>
          )}

          {tableError && (
            <Text type={'danger'} style={{ display: 'block', marginTop: 8 }}>{tableError}</Text>
          )}

          <Row justify={'end'} align={'middle'} gutter={8} style={{ marginTop: 16 }}>
            <Col>
              <Form.Item label={<Text strong style={{ fontSize: '16px' }}>Total</Text>}>
                <Input value={formatNumberForDisplay(calculateGrandTotal())} readOnly style={{ width: 140 }} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label={<Text strong style={{ fontSize: '16px', color: 'transparent' }}>Guardar</Text>}>
                <Button type={'primary'} icon={<SaveOutlined />} onClick={handleSave}>
                  Guardar
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
    </>
  );
}
